import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUsers } from '../api/userAccounts';
import { useDebounce } from '../hooks';
import { PLAN_FILTERS, STATUS_FILTERS } from '../types/userAccount';
import type { UserAccountListItem, UserAccountFilters } from '../types/userAccount';
import styles from './AccountsList.module.css';

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function getStatusInfo(user: UserAccountListItem): { label: string; className: string } {
  if (user.subscription?.active && user.subscription.status === 'active') {
    return { label: 'Active', className: styles.badgeGreen };
  }
  if (user.subscription?.status === 'succeeded') {
    return { label: 'Lifetime', className: styles.badgePurple };
  }
  if (user.subscription?.status === 'canceled') {
    return { label: 'Canceled', className: styles.badgeRed };
  }
  if (user.is_trial_active) {
    return { label: 'Trial', className: styles.badgeBlue };
  }
  if (user.has_used_trial && !user.is_trial_active && !user.subscription) {
    return { label: 'Expired', className: styles.badgeGray };
  }
  if (!user.has_used_trial && !user.subscription) {
    return { label: 'No Plan', className: styles.badgeGray };
  }
  return { label: 'Unknown', className: styles.badgeGray };
}

export default function AccountsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = searchParams.get('search') || '';
  const plan = searchParams.get('plan') || '';
  const status = searchParams.get('status') || '';
  const ordering = searchParams.get('ordering') || '-id';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const pageSize = 25;

  const [searchInput, setSearchInput] = useState(urlSearch);
  const [users, setUsers] = useState<UserAccountListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadIdRef = useRef(0);

  const debouncedSearch = useDebounce(searchInput, 300);

  const setParam = useCallback((updates: Record<string, string | null>, resetPage = true) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      if (resetPage && !('page' in updates)) next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Sync debounced search to URL
  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current === debouncedSearch) return;
    prevSearchRef.current = debouncedSearch;
    setParam({ search: debouncedSearch || null });
  }, [debouncedSearch, setParam]);

  const loadUsers = useCallback(async () => {
    const thisLoad = ++loadIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const filters: UserAccountFilters = {
        page,
        page_size: pageSize,
        ordering,
      };
      if (debouncedSearch) filters.search = debouncedSearch;
      if (plan) filters.plan = plan as UserAccountFilters['plan'];
      if (status) filters.status = status as UserAccountFilters['status'];

      const data = await getUsers(filters);
      if (thisLoad !== loadIdRef.current) return;
      setUsers(data.results);
      setTotalCount(data.count);
    } catch {
      if (thisLoad !== loadIdRef.current) return;
      setError('Failed to load users');
    } finally {
      if (thisLoad === loadIdRef.current) setIsLoading(false);
    }
  }, [page, pageSize, ordering, debouncedSearch, plan, status]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>User Accounts</h1>
          <p className={styles.subtitle}>{totalCount.toLocaleString()} users</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshButton} onClick={loadUsers} disabled={isLoading}>
            <span className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search by email..."
            className={styles.searchInput}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button className={styles.clearSearchButton} onClick={() => setSearchInput('')} title="Clear search">
              ✕
            </button>
          )}
        </div>
        <div className={styles.filterGroup}>
          <select className={styles.select} value={plan} onChange={(e) => setParam({ plan: e.target.value || null })}>
            {PLAN_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select className={styles.select} value={status} onChange={(e) => setParam({ status: e.target.value || null })}>
            {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select className={styles.select} value={ordering} onChange={(e) => setParam({ ordering: e.target.value })}>
            <option value="-id">Newest First</option>
            <option value="id">Oldest First</option>
            <option value="email">Email A-Z</option>
            <option value="-email">Email Z-A</option>
            <option value="-trial_start_date">Trial Start (Newest)</option>
            <option value="trial_start_date">Trial Start (Oldest)</option>
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.retryButton} onClick={loadUsers}>Retry</button>
        </div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && users.length === 0 ? (
          <div className={styles.loading}>Loading users...</div>
        ) : users.length === 0 ? (
          <div className={styles.emptyState}>No users found matching your filters.</div>
        ) : (
          <>
            <table className={`${styles.table} ${isLoading ? styles.tableLoading : ''}`}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Trial</th>
                  <th>Devices</th>
                  <th>Trial Start</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const si = getStatusInfo(user);
                  return (
                    <tr key={user.id} className={styles.clickableRow} onClick={() => navigate(`/accounts/${user.id}`)}>
                      <td className={styles.emailCell}>
                        <span className={styles.email}>{user.email}</span>
                        {user.is_staff && <span className={styles.staffBadge}>Staff</span>}
                      </td>
                      <td>
                        <span className={`${styles.badge} ${si.className}`}>{si.label}</span>
                      </td>
                      <td className={styles.planCell}>
                        {user.subscription ? user.subscription.plan_name : '-'}
                      </td>
                      <td className={styles.trialCell}>
                        {user.is_trial_active ? (
                          <span className={styles.trialActive}>
                            Active
                            {user.free_plan_expiry_date && (
                              <span className={styles.trialExpiry}> (expires {formatDate(user.free_plan_expiry_date)})</span>
                            )}
                          </span>
                        ) : user.has_used_trial ? (
                          <span className={styles.trialUsed}>Used</span>
                        ) : (
                          <span className={styles.trialNone}>Not started</span>
                        )}
                      </td>
                      <td className={styles.numCell}>{user.devices_count}</td>
                      <td>
                        <div className={styles.timestamp}>
                          <span className={styles.timestampFull}>{formatDate(user.trial_start_date)}</span>
                          <span className={styles.timestampRelative}>{formatRelative(user.trial_start_date)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className={styles.mobileCards}>
              {users.map(user => {
                const si = getStatusInfo(user);
                return (
                  <div key={user.id} className={styles.mobileCard} onClick={() => navigate(`/accounts/${user.id}`)}>
                    <div className={styles.mobileCardHeader}>
                      <span className={styles.email}>{user.email}</span>
                      <span className={`${styles.badge} ${si.className}`}>{si.label}</span>
                    </div>
                    <div className={styles.mobileCardBody}>
                      <span>{user.subscription?.plan_name || 'No plan'}</span>
                      <span>{user.devices_count} device{user.devices_count !== 1 ? 's' : ''}</span>
                      <span>Trial {formatDate(user.trial_start_date)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            onClick={() => setParam({ page: String(Math.max(1, page - 1)) }, false)}
            disabled={page === 1 || isLoading}
          >
            ← Previous
          </button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button
            className={styles.pageButton}
            onClick={() => setParam({ page: String(Math.min(totalPages, page + 1)) }, false)}
            disabled={page === totalPages || isLoading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
